/**
 * NPUniverse Apps Catalog
 * Metadata for all apps in the NPUniverse ecosystem
 * 
 * CALIBRATION: Umbrales calibrados para llama-68m basados en datos reales:
 * - Samsung S9+: ~16 T/s → BASIC
 * - Pixel 7 Pro: ~41 T/s → FLUID
 * - Huawei P10 Lite: ~3 T/s → LIMITED
 * 
 * Thresholds: TITAN>=100, FLUID>=50, BASIC>=20, LIMITED<20
 */

export const APP_STATUS = Object.freeze({
  LIVE: 'live',
  COMING_SOON: 'coming_soon',
  PLANNED: 'planned',
  RESEARCH: 'research'
});

export const APP_TYPE = Object.freeze({
  APP: 'app',
  TOOL: 'tool'
});

export const DOWNLOAD_TYPE = Object.freeze({
  STORE: 'store',
  DIRECT: 'direct',
  WEB: 'web',
  SOURCE: 'source'
});

export const NPUNIVERSE_APPS = Object.freeze([
  {
    id: 'npu_check',
    name: 'NPU Check',
    icon: '🔍',
    type: APP_TYPE.APP,
    tagline: 'features.npu_check.tagline',
    description: 'features.npu_check.desc',
    features: ['features.npu_check.feature_1', 'features.npu_check.feature_2', 'features.npu_check.feature_3'],
    minScore: 1,
    optimalScore: 30,
    status: APP_STATUS.LIVE,
    ctaText: 'nav.download',
    priority: 2,
    pageRoute: 'npu-check',
    githubRepo: 'Ericson246/NPU-Check-Private',
    privateRepo: true,
    category: 'benchmark',
    downloadLinks: [
      {
        platform: 'android',
        label: 'Android (Play Store)',
        type: DOWNLOAD_TYPE.STORE,
        url: 'https://play.google.com/store/apps/details?id=com.npucheck.app'
      },
      {
        platform: 'windows',
        label: 'Windows (Store)',
        type: DOWNLOAD_TYPE.STORE,
        url: 'ms-windows-store://pdp/?productid=9N2DL425CNNQ'
      },
      {
        platform: 'android',
        label: 'Android APK',
        type: DOWNLOAD_TYPE.DIRECT,
        url: 'https://objectstorage.eu-madrid-1.oraclecloud.com/n/axycxr8vzp5l/b/npu-universe-downloads/o/npucheck%2Fapp-release.apk'
      },
      {
        platform: 'android',
        label: 'Android AAB',
        type: DOWNLOAD_TYPE.DIRECT,
        url: 'https://objectstorage.eu-madrid-1.oraclecloud.com/n/axycxr8vzp5l/b/npu-universe-downloads/o/npucheck%2Fapp-release.aab'
      },
      {
        platform: 'windows',
        label: 'Windows Portable',
        type: DOWNLOAD_TYPE.DIRECT,
        url: 'https://objectstorage.eu-madrid-1.oraclecloud.com/n/axycxr8vzp5l/b/npu-universe-downloads/o/npucheck%2FNPU-Check-Windows-Portable.zip'
      },
      {
        platform: 'windows',
        label: 'Windows MSIX',
        type: DOWNLOAD_TYPE.DIRECT,
        url: 'https://objectstorage.eu-madrid-1.oraclecloud.com/n/axycxr8vzp5l/b/npu-universe-downloads/o/npucheck%2Fnpu_check.msix'
      },
      {
        platform: 'linux',
        label: 'Linux x64',
        type: DOWNLOAD_TYPE.DIRECT,
        url: 'https://objectstorage.eu-madrid-1.oraclecloud.com/n/axycxr8vzp5l/b/npu-universe-downloads/o/npucheck%2FNPU-Check-Linux-x64.tar.gz'
      },
      {
        platform: 'web',
        label: 'Web Benchmark',
        type: DOWNLOAD_TYPE.WEB,
        route: '/benchmark'
      }
    ]
  },
  {
    id: 'npu_agent',
    name: 'NPU Agent',
    icon: '🤖',
    type: APP_TYPE.APP,
    tagline: 'apps.npu_agent.tagline',
    description: 'apps.npu_agent.description',
    features: ['apps.npu_agent.features.0', 'apps.npu_agent.features.1', 'apps.npu_agent.features.2'],
    minScore: 20,
    optimalScore: 60,
    status: APP_STATUS.LIVE,
    ctaText: 'nav.download',
    priority: 1,
    pageRoute: 'npu-agent',
    githubRepo: 'Ericson246/NPU-Agent',
    privateRepo: true,
    category: 'agent',
    downloadLinks: [
      {
        platform: 'android',
        label: 'Android APK',
        type: DOWNLOAD_TYPE.DIRECT,
        url: 'https://objectstorage.eu-madrid-1.oraclecloud.com/n/axycxr8vzp5l/b/npu-universe-downloads/o/npu-agent%2Fapp-release.apk'
      },
      {
        platform: 'android',
        label: 'Android AAB',
        type: DOWNLOAD_TYPE.DIRECT,
        url: 'https://objectstorage.eu-madrid-1.oraclecloud.com/n/axycxr8vzp5l/b/npu-universe-downloads/o/npu-agent%2Fapp-release.aab'
      },
      {
        platform: 'windows',
        label: 'Windows ZIP',
        type: DOWNLOAD_TYPE.DIRECT,
        url: 'https://objectstorage.eu-madrid-1.oraclecloud.com/n/axycxr8vzp5l/b/npu-universe-downloads/o/npu-agent%2Fnpu-agent-windows.zip'
      },
      {
        platform: 'windows',
        label: 'Windows MSIX',
        type: DOWNLOAD_TYPE.DIRECT,
        url: 'https://objectstorage.eu-madrid-1.oraclecloud.com/n/axycxr8vzp5l/b/npu-universe-downloads/o/npu-agent%2Fnpu_agent.msix'
      },
      {
        platform: 'linux',
        label: 'Linux',
        type: DOWNLOAD_TYPE.DIRECT,
        url: 'https://objectstorage.eu-madrid-1.oraclecloud.com/n/axycxr8vzp5l/b/npu-universe-downloads/o/npu-agent%2Fnpu-agent-linux.tar.gz'
      }
    ]
  },
  {
    id: 'infinia_chat',
    name: 'NPU Chat',
    icon: '💬',
    type: APP_TYPE.APP,
    tagline: 'apps.infinia_chat.tagline',
    description: 'apps.infinia_chat.description',
    features: ['apps.infinia_chat.features.0', 'apps.infinia_chat.features.1', 'apps.infinia_chat.features.2'],
    // Mínimo: BASIC (20 T/s) -chat básico funcional
    minScore: 20,
    // Óptimo: FLUID (50 T/s) -chat fluido
    optimalScore: 50,
    status: APP_STATUS.COMING_SOON,
    launchQuarter: 'Q4 2026',
    ctaText: 'apps.infinia_chat.cta',
    priority: 3,
    pageRoute: 'npu-chat',
    githubRepo: '',
    category: 'chat'
  },
  {
    id: 'npu_optimize',
    name: 'NPU Optimize',
    icon: '⚙️',
    type: APP_TYPE.TOOL,
    tagline: 'apps.npu_optimize.tagline',
    description: 'apps.npu_optimize.description',
    features: ['apps.npu_optimize.features.0', 'apps.npu_optimize.features.1', 'apps.npu_optimize.features.2'],
    minScore: 0,
    optimalScore: 1,
    status: APP_STATUS.LIVE,
    ctaText: 'apps.npu_optimize.cta',
    priority: 10,
    pageRoute: 'npu-optimize',
    githubRepo: 'Ericson246/npu-optimize',
    category: 'cli',
    excludeFromRecommendations: true,
    documentationPath: 'README.md',
    downloadLinks: [
      {
        platform: 'source',
        label: 'GitHub Releases',
        type: DOWNLOAD_TYPE.SOURCE,
        url: 'https://github.com/Ericson246/npu-optimize/releases'
      }
    ]
  },
  {
    id: 'npu_check_ios',
    name: 'NPU Check (iOS)',
    icon: '📊',
    type: APP_TYPE.APP,
    tagline: 'features.npu_check.tagline',
    description: 'features.npu_check.desc',
    features: ['features.npu_check.feature_1', 'features.npu_check.feature_2', 'features.npu_check.feature_3'],
    minScore: 0,
    optimalScore: 1,
    status: APP_STATUS.COMING_SOON,
    launchQuarter: 'Q2 2026',
    ctaText: 'benchmark.let_me_know',
    priority: 100, // No aparecer en recomendaciones automáticas por score
    excludeFromRecommendations: true,
    hideFromCatalog: true,
    pageRoute: 'npu-check-ios'
  }

]);

/**
 * Get compatible apps for a given performance score
 * @param {number} score - Device performance score (T/s)
 * @returns {Array} Compatible apps sorted by priority
 */
export const getCompatibleApps = (score) => {
  return NPUNIVERSE_APPS
    .filter(app => score >= app.minScore && !app.excludeFromRecommendations && app.type === APP_TYPE.APP)
    .map(app => ({
      ...app,
      performanceLevel: getPerformanceLevel(score, app)
    }))
    .sort((a, b) => a.priority - b.priority);
};

/**
 * Get the recommended app (highest priority that's compatible)
 * @param {number} score - Device performance score (T/s)
 * @returns {Object} Recommended app (always returns at least InfinIA Chat)
 */
export const getRecommendedApp = (score) => {
  const compatible = getCompatibleApps(score);
  
  // Always return at least InfinIA Chat as fallback
  if (compatible.length > 0) {
    return compatible[0];
  }
  
  // Fallback: return InfinIA Chat for very low-end devices
  return {
    ...NPUNIVERSE_APPS[0], // InfinIA Chat
    performanceLevel: 'basic'
  };
};

/**
 * Calculate performance level for an app given user's score
 * @param {number} score - User's device score
 * @param {Object} app - App object
 * @returns {string} Performance level: 'excellent', 'good', or 'basic'
 */
const getPerformanceLevel = (score, app) => {
  if (score >= app.optimalScore * 1.2) return 'excellent';
  if (score >= app.optimalScore) return 'good';
  return 'basic';
};

/**
 * Get secondary apps (compatible but not recommended)
 * @param {number} score - Device performance score (T/s)
 * @returns {Array} Secondary apps
 */
export const getSecondaryApps = (score) => {
  const compatible = getCompatibleApps(score);
  return compatible.slice(1); // All except the first (recommended)
};

export const getPublicCatalog = () => NPUNIVERSE_APPS
  .filter((item) => !item.hideFromCatalog)
  .sort((a, b) => a.priority - b.priority);

export const getCatalogApps = () => getPublicCatalog().filter((item) => item.type === APP_TYPE.APP);

export const getCatalogTools = () => getPublicCatalog().filter((item) => item.type === APP_TYPE.TOOL);

export const getLiveApps = () => getCatalogApps().filter((item) => item.status === APP_STATUS.LIVE);

export const getComingSoonApps = () => getCatalogApps().filter((item) => item.status === APP_STATUS.COMING_SOON);

export const findCatalogItemById = (id) => NPUNIVERSE_APPS.find((item) => item.id === id);

export const findCatalogItemByRoute = (route) => NPUNIVERSE_APPS.find((item) => item.pageRoute === route);
